import makeStyles from '@mui/styles/makeStyles';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { CalendarDays } from "lucide-react";
import { noop } from 'lodash';
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import pickerTheme, { pickerPopperProps } from "common/pickerTheme";

const useOutlinedInputStyles = makeStyles(theme => ({
  root: ({ showBorder }) => ({
    backgroundColor: 'white',
    borderRadius: 6,
    boxShadow: showBorder
      ? '0 0 0 1px rgba(0, 0, 0, 0.1)'
      : '0px 0px 2px rgba(0, 0, 0, 0.1) inset',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    "&:hover:not($disabled):not($focused):not($error) $notchedOutline": {
      borderColor: showBorder ? '#999' : 'transparent',
    },
  }),
  notchedOutline: ({ showBorder }) => ({
    border: showBorder ? '1px solid #ddd !important' : '0px !important',
  }),
  disabled: {},
  focused: {},
  error: {},
}));

// The whole field is the trigger — clicking anywhere on it opens the calendar,
// and the value can still be typed while the calendar is open. The year view is
// reached from the month label in the calendar header.
const DateSelection = ({
  onAnswered = noop(),
  value = null,
  disabled = false,
  showBorder = false,
  openTo = 'day',
}) => {
  const { t } = useTranslation();
  const classes = useOutlinedInputStyles({ showBorder });
  const [open, setOpen] = useState(false);

  // Question data still carries the v3 name for the day view.
  const openToView = openTo === 'date' ? 'day' : openTo;

  const handleDateChange = date => {
    if (date) {
      onAnswered(date.format("YYYY-MM-DD"));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={pickerTheme}>
          <DatePicker
            inputFormat="DD/MM/YYYY"
            value={value}
            disableFuture={false}
            onChange={handleDateChange}
            openTo={openToView}
            dayOfWeekFormatter={day => day}
            disabled={disabled}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            PopperProps={pickerPopperProps}
            components={{ OpenPickerIcon: () => <CalendarDays size={18} strokeWidth={2}/> }}
            OpenPickerButtonProps={{ 'aria-label': 'Select Date' }}
            renderInput={params => (
              <TextField
                {...params}
                id="date-picker-dialog"
                fullWidth
                variant="outlined"
                onClick={() => !disabled && setOpen(true)}
                inputProps={{
                  ...params.inputProps,
                  placeholder: `${t('selectDate')} (DD/MM/YYYY)`,
                }}
                InputProps={{
                  ...params.InputProps,
                  classes: {
                    root: classes.root,
                    notchedOutline: classes.notchedOutline,
                  },
                }}
              />
            )}
          />
        </ThemeProvider>
      </StyledEngineProvider>
    </LocalizationProvider>
  );
};

export default DateSelection;
